/**
 * @author       justin.jeong@zumper.com (Justin Jeong)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  error filter helpers for API calls and errorBoundary
 */

import { isCookieEnabled } from './utils'

const xzTokenReg = /xz_token/i
const csrfTokenReg = /csrf/i

function isDataObject(error: unknown): error is { data: object } {
  return (
    !!error &&
    typeof error === 'object' &&
    'data' in error &&
    !!error.data &&
    typeof error.data === 'object'
  )
}

function isDataReasonObject(error: unknown): error is { data: { reason: string } } {
  return isDataObject(error) && 'reason' in error.data && typeof error.data.reason === 'string'
}

export interface DataDetail {
  ctx: unknown
  input: unknown
  loc: readonly string[]
  msg: string
  type: string
  url: string
}

export function isDataDetailObject(
  error: unknown
): error is { data: { detail: readonly DataDetail[] } } {
  return !!(
    isDataObject(error) &&
    'detail' in error.data &&
    Array.isArray(error.data.detail) &&
    error.data.detail.length &&
    typeof error.data.detail[0] === 'object' &&
    error.data.detail[0] &&
    'loc' in error.data.detail[0]
  )
}

export const getValidationErrors = (error: unknown) => {
  if (isDataDetailObject(error)) {
    return error.data.detail
      .map((detail) => {
        const locString = detail.loc.join('.')
        return `${locString}=${detail.input} (${detail.msg})`
      })
      .join(', ')
  }
}

function isMessageObject(error: unknown): error is { message: string } {
  return (
    !!error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
  )
}

export const isCommonAuthError = (error: unknown): ReturnType<typeof isDataReasonObject> => {
  if (isDataReasonObject(error)) {
    return xzTokenReg.test(error.data.reason) || csrfTokenReg.test(error.data.reason)
  }
  return false
}

export const isWebGLError = (error: unknown) => {
  return (
    isMessageObject(error) &&
    (error.message.includes('Failed to initialize WebGL') ||
      error.message.includes("Failed to execute 'shaderSource'"))
  )
}

export const isPriceDataResourceError = (error: unknown) => {
  return isDataObject(error) && 'status' in error.data && error.data.status === 404
}

export const isCookieError = (error: unknown): boolean => {
  if (isDataReasonObject(error)) {
    return error.data.reason.includes('cookie not set') && !isCookieEnabled
  }
  return false
}

export const isFailedToFetchError = (error: unknown) =>
  isMessageObject(error) && error.message.includes('Failed to fetch')

// This is for failing chunks for the price chart in the rent trends loadable section.
export const isPriceChartFailedChunkError = (error: unknown) =>
  isMessageObject(error) &&
  error.message.includes('Property description must be an object: undefined')

export const isRateLimitError = (error: unknown) => {
  return !!error && typeof error === 'object' && 'status' in error && error.status === 429
}
